import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../lib/auth.context';
import { useRedirectUrl } from '../hooks/useRedirectUrl';
import { useToast } from '../hooks/use-toast';
import { useFeedback } from '../hooks/useFeedback';
import { Cog6ToothIcon } from '@heroicons/react/24/outline';
import { LogOutIcon } from 'lucide-react';

export const Navigation = () => {
  const location = useLocation();
  const { logout, isAuthenticated } = useAuth();
  const { storeCurrentUrlAndRedirect } = useRedirectUrl();
  const { toast } = useToast();
  
  const handleLogout = async () => {
    try {
      await logout();
      storeCurrentUrlAndRedirect('/login');
      toast({
        title: 'Logged out',
        description: 'You have been successfully logged out.',
      });
    } catch (error) {
      console.error('Logout error:', error);
      toast({
        title: 'Error',
        description: 'Failed to log out. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleSettingsClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (!isAuthenticated) {
      e.preventDefault();
      storeCurrentUrlAndRedirect('/login');
      toast({
        title: 'Authentication required',
        description: 'Please log in to access settings.',
      });
    }
  };
  
  const { openDialog } = useFeedback();

  const handleFeedbackClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (!isAuthenticated) {
      e.preventDefault();
      storeCurrentUrlAndRedirect('/login');
      toast({
        title: 'Authentication required',
        description: 'Please log in to provide feedback.',
      });
    }
  };

  return (
    <nav className="bg-black text-[#CECFD2] shadow-md" style={{ fontFamily: 'Text md/Semibold, sans-serif', fontSize: '14px' }}>
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center h-14">
          <div className="flex items-center mr-4">
            <Link to="/">
              <img src="/logo.png" alt="Club Logo" className="h-8" />
            </Link>
          </div>
          <div className="flex space-x-2 flex-grow">
            <Link 
              to="/" 
              className={`inline-flex items-center px-3 py-2 font-semibold rounded-md ${
                location.pathname === '/' 
                  ? 'bg-[#26222E] text-[#CECFD2]' 
                  : 'hover:bg-gray-900 hover:text-gray-300'
              }`}
            >
              My Club
            </Link>
            <Link 
              to="/kyp-search" 
              className={`inline-flex items-center px-3 py-2 font-semibold rounded-md ${
                location.pathname === '/kyp-search' 
                  ? 'bg-[#26222E] text-[#CECFD2]' 
                  : 'hover:bg-gray-900 hover:text-gray-300'
              }`}
            >
              KYP Search
            </Link>
            <Link
              to="/rag-documents"
              className={`inline-flex items-center px-3 py-2 font-semibold rounded-md ${
                location.pathname === '/rag-documents' 
                  ? 'bg-[#26222E] text-[#CECFD2]' 
                  : 'hover:bg-gray-900 hover:text-gray-300'
              }`}
            >
              Document Q&A
            </Link>
            <Link 
              to="/oad-search" 
              className={`inline-flex items-center px-3 py-2 font-semibold rounded-md ${
                location.pathname === '/oad-search' 
                  ? 'bg-[#26222E] text-[#CECFD2]' 
                  : 'hover:bg-gray-900 hover:text-gray-300'
              }`}
              aria-label="OAD Search"
              role="menuitem"
            >
              OAD Search
            </Link>
            
            <div className="ml-2">
              {isAuthenticated && (
                <>
                  <Link
                    to="/settings"
                    onClick={handleSettingsClick}
                    className={`inline-flex items-center px-3 py-2 font-semibold rounded-md ${
                      location.pathname === '/settings' 
                        ? 'bg-[#26222E] text-[#CECFD2]' 
                        : 'hover:bg-gray-900 hover:text-gray-300'
                    }`}
                    aria-label="Settings"
                    role="menuitem"
                  >
                    <Cog6ToothIcon className="h-5 w-5" />
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="inline-flex items-center px-3 py-2 font-semibold rounded-md hover:bg-gray-900 hover:text-gray-300"
                    aria-label="Logout"
                  >
                    <LogOutIcon className="h-5 w-5" />
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};